/**
 * Browser-side preparation of a library upload (docs/library.md): what kind of file it is, the platform limits
 * we can tell the user about before uploading, a jpeg preview (LINE previewImageUrl, ≤ 1 MB — 1024 px so it is
 * still sharp in the LINE chat bubble on a phone; a 240 px preview looks blurry there), a video poster frame +
 * size/duration, webp → jpg (LINE does not show webp), and the sha256 used to find duplicates.
 * DOM-only helpers are kept apart from the pure ones so tests can use the latter in Node.
 */
import type { MediaAsset, MediaKind } from '../api/types'

export const MB = 1024 * 1024
export const LIMIT_BYTES: Record<MediaKind, number> = { image: 10 * MB, video: 200 * MB }
export const ACCEPT = 'image/jpeg,image/png,image/webp,video/mp4'
const MIMES: Record<string, MediaKind> = { 'image/jpeg': 'image', 'image/png': 'image', 'image/webp': 'image', 'video/mp4': 'video' }
const PREVIEW_SIDE = 1024
const POSTER_SIDE = 1024
const PREVIEW_MAX_BYTES = 900 * 1024 // LINE: previewImageUrl ≤ 1 MB
const HASH_MAX = 60 * MB

/** A file ready to upload: the (possibly converted) bytes + what the API needs to know about them. */
export interface PreparedFile {
  name: string
  title: string
  kind: MediaKind
  blob: Blob
  preview: Blob | null
  width: number | null
  height: number | null
  duration_ms: number | null
  sha256: string | null
  /** e.g. "แปลงรูป webp เป็น jpg แล้ว เพื่อให้ส่งใน LINE ได้" */
  notes: string[]
}

export function kindOf(mime: string): MediaKind | null {
  return MIMES[mime.toLowerCase()] ?? null
}

export function sizeLabel(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  const v = bytes / MB
  return `${v < 10 ? v.toFixed(1).replace(/\.0$/, '') : Math.round(v)} MB`
}

export function durationLabel(ms: number | null): string | null {
  if (!ms || ms <= 0) return null
  const s = Math.round(ms / 1000)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** Title from a file name: no extension, underscores/dashes → spaces. */
export function titleFromName(name: string): string {
  const base = name.replace(/\.[A-Za-z0-9]{1,5}$/, '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
  return base.slice(0, 120)
}

/** Why this file cannot go into the library (Thai), or null. Same rules as api/app/media_store.py. */
export function fileProblem(file: { type: string; size: number; name: string }): string | null {
  const kind = kindOf(file.type)
  if (!kind) return `“${file.name}” ไม่ใช่รูป (jpg, png, webp) หรือวิดีโอ (mp4)`
  if (file.size <= 0) return `“${file.name}” เป็นไฟล์ว่าง`
  if (file.size > LIMIT_BYTES[kind]) {
    const label = kind === 'image' ? 'รูป' : 'วิดีโอ'
    return `${label} “${file.name}” ใหญ่ ${sizeLabel(file.size)} เกินที่ LINE รับได้ (${sizeLabel(LIMIT_BYTES[kind])})`
  }
  return null
}

/** The note shown when a library file cannot go out on this chat's channel (null = fine). */
export function channelBlock(asset: Pick<MediaAsset, 'limits'>, channel: string | null | undefined): string | null {
  if (!channel) return null
  return asset.limits.find((l) => l.channel === channel)?.reason ?? null
}

export async function sha256Hex(blob: Blob): Promise<string | null> {
  if (blob.size > HASH_MAX || !globalThis.crypto?.subtle) return null
  const buf = await blob.arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', buf)
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

// ---------------------------------------------------------------- DOM helpers (browser only)

export function canvasBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), type, quality),
  )
}

/** Give a canvas's memory back now (0 × 0): iOS Safari counts every live canvas against one total limit until
 *  garbage collection, which a batch of uploads can reach. */
export function releaseCanvas(c: HTMLCanvasElement): void {
  c.width = 0
  c.height = 0
}

/** jpeg ≤ PREVIEW_MAX_BYTES: lower the quality, then the size, until it fits. */
export async function previewJpeg(src: CanvasImageSource, w: number, h: number, side: number): Promise<Blob> {
  let s = side
  for (let round = 0; round < 4; round++) {
    const c = draw(src, w, h, s)
    try {
      for (const q of [0.82, 0.7, 0.6]) {
        const b = await canvasBlob(c, 'image/jpeg', q)
        if (b.size <= PREVIEW_MAX_BYTES) return b
      }
    } finally {
      releaseCanvas(c)
    }
    s = Math.round(s * 0.75)
  }
  const c = draw(src, w, h, 480)
  try {
    return await canvasBlob(c, 'image/jpeg', 0.6)
  } finally {
    releaseCanvas(c)
  }
}

function fit(w: number, h: number, side: number): [number, number] {
  const k = Math.min(1, side / Math.max(w, h))
  return [Math.max(1, Math.round(w * k)), Math.max(1, Math.round(h * k))]
}

export async function loadImage(blob: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(blob)
  try {
    const img = new Image()
    img.decoding = 'async'
    img.src = url
    await img.decode()
    return img
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
}

function draw(src: CanvasImageSource, w: number, h: number, side: number): HTMLCanvasElement {
  const [cw, ch] = fit(w, h, side)
  const c = document.createElement('canvas')
  c.width = cw
  c.height = ch
  const ctx = c.getContext('2d')
  if (!ctx) throw new Error('no canvas')
  ctx.fillStyle = '#ffffff' // transparent PNG → white, not black, in the jpeg preview
  ctx.fillRect(0, 0, cw, ch)
  ctx.drawImage(src, 0, 0, cw, ch)
  return c
}

async function prepareImage(file: File): Promise<PreparedFile> {
  const img = await loadImage(file)
  const w = img.naturalWidth
  const h = img.naturalHeight
  let blob: Blob = file
  const notes: string[] = []
  if (file.type === 'image/webp') {
    blob = await canvasBlob(draw(img, w, h, Math.max(w, h)), 'image/jpeg', 0.9)
    notes.push('แปลงรูป webp เป็น jpg แล้ว เพื่อให้ส่งใน LINE ได้')
  }
  const preview = await previewJpeg(img, w, h, PREVIEW_SIDE)
  return { name: file.name, title: titleFromName(file.name), kind: 'image', blob, preview, width: w, height: h,
           duration_ms: null, sha256: await sha256Hex(blob), notes }
}

async function prepareVideo(file: File): Promise<PreparedFile> {
  const url = URL.createObjectURL(file)
  const v = document.createElement('video')
  v.muted = true
  v.playsInline = true
  v.preload = 'auto'
  v.src = url
  try {
    await new Promise<void>((resolve, reject) => {
      v.onloadeddata = () => resolve()
      v.onerror = () => reject(new Error('video'))
    })
    const duration = Number.isFinite(v.duration) ? Math.round(v.duration * 1000) : null
    await new Promise<void>((resolve) => {
      v.onseeked = () => resolve()
      v.currentTime = Math.min(1, (v.duration || 2) / 4)
    })
    const poster = await previewJpeg(v, v.videoWidth, v.videoHeight, POSTER_SIDE)
    return { name: file.name, title: titleFromName(file.name), kind: 'video', blob: file, preview: poster,
             width: v.videoWidth || null, height: v.videoHeight || null, duration_ms: duration,
             sha256: await sha256Hex(file), notes: [] }
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** Read the file in the browser: sizes, preview/poster, webp → jpg, sha256. Throws a Thai Error when unreadable. */
export async function prepareFile(file: File): Promise<PreparedFile> {
  const problem = fileProblem(file)
  if (problem) throw new Error(problem)
  try {
    return kindOf(file.type) === 'video' ? await prepareVideo(file) : await prepareImage(file)
  } catch {
    throw new Error(`เปิดไฟล์ “${file.name}” ไม่ได้ ไฟล์อาจเสีย ลองบันทึกใหม่แล้วอัปโหลดอีกครั้ง`)
  }
}
